/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { logger } from '@/utils/logger';

interface PerformanceMetrics {
  longTasks: number;
  memoryUsage?: number;
  renderTime: number;
}

export function usePerformanceMonitor() {
  const metricsRef = useRef<PerformanceMetrics>({
    longTasks: 0,
    renderTime: 0
  });
  const observerRef = useRef<PerformanceObserver | null>(null);

  useEffect(() => {
    // Désactiver complètement en production pour les performances
    if (process.env.NODE_ENV !== 'development') return;

    // Observer pour les tâches longues (>50ms sur mobile)
    if ('PerformanceObserver' in window) {
      observerRef.current = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.duration > 80) { // Seuil plus élevé pour réduire le bruit
            metricsRef.current.longTasks++;
            // Réduire les logs en production
            if (process.env.NODE_ENV === 'development') {
              logger.warn(`⚠️ Tâche bloquante: ${entry.duration.toFixed(2)}ms`);
            }
          }
        });
      });

      try {
        observerRef.current.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        logger.warn('Long task observer non supporté');
      }
    }

    // Surveillance mémoire optimisée
    const monitorMemory = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        const usedMB = Math.round(memory.usedJSHeapSize / (1024 * 1024));
        
        if (usedMB > 80) { // Seuil plus élevé
          if (process.env.NODE_ENV === 'development') {
            logger.warn(`📱 Mémoire: ${usedMB}MB`);
          }
        }
        
        metricsRef.current.memoryUsage = usedMB;
      }
    };

    const memoryInterval = setInterval(monitorMemory, 30000); // Réduit encore la fréquence

    return () => {
      observerRef.current?.disconnect();
      clearInterval(memoryInterval);
    };
  }, []);

  return metricsRef.current;
}